import React, { useContext, useEffect, useState } from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Timer,
  BookMarked,
  ScrollText,
  Route,
  MapPin,
  Settings,
  Map,
  Compass,
  Castle,
  Sword,
  Moon,
  Clock3,
  Gem,
  Users,
  NotebookPen,
  WandSparkles,
  Crown,
  Layers3,
  Milestone,
} from "lucide-react";

import Button from "../../components/ui/Button";
import { UIContext } from "./uiContext";

const STEPS = [
  {
    title: "Benvenuto in Cartomancer",
    subtitle: "Il tuo grimorio per le campagne di D&D",
    icon: <WandSparkles className="h-9 w-9" />,
    accent: "text-gold",

    features: [
      {
        icon: <Map className="h-5 w-5" />,
        title: "Mappe",
        text: "Esplora, annota e custodisci i tuoi mondi.",
      },
      {
        icon: <Compass className="h-5 w-5" />,
        title: "Viaggi",
        text: "Pianifica gli spostamenti dei tuoi avventurieri.",
      },
      {
        icon: <ScrollText className="h-5 w-5" />,
        title: "Cronache",
        text: "Conserva la memoria delle avventure.",
      },
    ],
  },

  {
    title: "Segna i luoghi",
    subtitle: "Dai un volto al tuo mondo",
    icon: <MapPin className="h-9 w-9" />,
    accent: "text-arcane",

    features: [
      {
        icon: <MapPin className="h-5 w-5" />,
        title: "13 categorie fantasy",
        text: "Città, dungeon, templi, castelli e molto altro.",
      },
      {
        icon: <Users className="h-5 w-5" />,
        title: "NPC e segreti",
        text: "Collega personaggi, tesori, hook narrativi e note del DM.",
      },
      {
        icon: <Gem className="h-5 w-5" />,
        title: "Informazioni nascoste",
        text: "Custodisci i dettagli che solo il DM dovrebbe conoscere.",
      },
    ],
  },

  {
    title: "Traccia i percorsi",
    subtitle: "La strada è parte dell'avventura",
    icon: <Route className="h-9 w-9" />,
    accent: "text-gold",

    features: [
      {
        icon: <Route className="h-5 w-5" />,
        title: "Disegna il viaggio",
        text: "Usa Path e traccia la rotta direttamente sulla mappa.",
      },
      {
        icon: <Compass className="h-5 w-5" />,
        title: "Configura ogni tappa",
        text: "Scegli ambiente, terreno, clima e mezzo di viaggio.",
      },
      {
        icon: <Clock3 className="h-5 w-5" />,
        title: "Tempo automatico",
        text: "La durata del viaggio viene calcolata automaticamente.",
      },
      {
        icon: <Sword className="h-5 w-5" />,
        title: "Eventi lungo la strada",
        text: "Aggiungi riposi, incontri e altri eventi tra le tappe.",
      },
    ],
  },

  {
    title: "L'orologio della campagna",
    subtitle: "Il tempo scorre anche quando gli eroi non combattono",
    icon: <Timer className="h-9 w-9" />,
    accent: "text-gold",

    features: [
      {
        icon: <Clock3 className="h-5 w-5" />,
        title: "Data e ora",
        text: "Segui il tempo in-game della tua campagna.",
      },
      {
        icon: <Moon className="h-5 w-5" />,
        title: "Giorno e notte",
        text: "La mappa cambia atmosfera quando cala la notte.",
      },
      {
        icon: <Sparkles className="h-5 w-5" />,
        title: "Riposi",
        text: "Riposo Breve +1h · Riposo Lungo +8h.",
      },
      {
        icon: <Settings className="h-5 w-5" />,
        title: "Personalizzalo",
        text: "Scegli lo stile dell'orologio dalle impostazioni.",
      },
    ],
  },

  {
    title: "Viaggi & Cronache",
    subtitle: "Ogni viaggio lascia una traccia",
    icon: <ScrollText className="h-9 w-9" />,
    accent: "text-gold",

    features: [
      {
        icon: <Clock3 className="h-5 w-5" />,
        title: "Conferma Spostamenti",
        text: "Completa il viaggio e fai avanzare l'orologio.",
      },
      {
        icon: <ScrollText className="h-5 w-5" />,
        title: "Salva Percorso",
        text: "Registra il viaggio senza modificare il tempo.",
      },
      {
        icon: <NotebookPen className="h-5 w-5" />,
        title: "Log di viaggio",
        text: "Ogni viaggio genera una cronaca esportabile in TXT.",
      },
    ],
  },

  {
    title: "Plasma il tuo grimorio",
    subtitle: "Cartomancer si adatta al tuo modo di giocare",
    icon: <Settings className="h-9 w-9" />,
    accent: "text-gold",

    features: [
      {
        icon: <Sparkles className="h-5 w-5" />,
        title: "Interfaccia",
        text: "Personalizza scala, dimensione del testo e colori.",
      },
      {
        icon: <Route className="h-5 w-5" />,
        title: "Viaggi",
        text: "Configura mezzi di viaggio e colori dei percorsi.",
      },
      {
        icon: <Timer className="h-5 w-5" />,
        title: "Tempo",
        text: "Personalizza orologio e calendario della campagna.",
      },
      {
        icon: <WandSparkles className="h-5 w-5" />,
        title: "Tutorial",
        text: "Richiama questa guida in qualsiasi momento.",
      },
    ],
  },

  // ─────────────────────────────────────────────
  // PREMIUM
  // ─────────────────────────────────────────────

  {
    title: "Il potere di Cartomancer",
    subtitle: "Più campagne, più mondi, un solo grimorio",
    icon: <Crown className="h-9 w-9" />,
    accent: "text-arcane",
    premium: true,

    features: [
      {
        icon: <Clock3 className="h-5 w-5" />,
        title: "Timeline",
        text: "Segui gli eventi della campagna nel corso del tempo.",
      },
      {
        icon: <ScrollText className="h-5 w-5" />,
        title: "Diario Narrativo",
        text: "Trasforma i viaggi in vere cronache della campagna.",
      },
      {
        icon: <BookMarked className="h-5 w-5" />,
        title: "Enciclopedia Wiki",
        text: "Costruisci e consulta la memoria del tuo mondo.",
      },
      {
        icon: <Layers3 className="h-5 w-5" />,
        title: "4 progetti",
        text: "Gestisci fino a 4 progetti, ideale per chi ha più ambientazioni.",
      },
    ],
  },

  {
    title: "Il viaggio continua",
    subtitle: "Cartomancer è ancora in evoluzione",
    icon: <Sparkles className="h-9 w-9" />,
    accent: "text-arcane",
    premium: true,
    finalPremium: true,

    features: [
      {
        icon: <WandSparkles className="h-5 w-5" />,
        title: "Nuove funzionalità",
        text: "Nuovi strumenti e possibilità sono già in fase di sviluppo.",
      },
      {
        icon: <Milestone className="h-5 w-5" />,
        title: "Nuovi orizzonti",
        text: "Cartomancer continuerà a crescere insieme alle esigenze dei Dungeon Master.",
      },
      {
        icon: <Sparkles className="h-5 w-5" />,
        title: "Miglioramenti continui",
        text: "Nuove funzioni, perfezionamenti e sorprese arriveranno nel tempo.",
      },
    ],
  },
];

export default function TutorialOverlay({
  onClose,
  onDontShowAgain,
}) {
  const [i, setI] = useState(0);
  const [direction, setDirection] = useState("next");

  const step = STEPS[i];

  const isFirst = i === 0;
  const isLast = i === STEPS.length - 1;

  const ctx = useContext(UIContext);

  useEffect(() => {
    ctx?.setIsBackgroundLocked?.(true);

    return () => {
      ctx?.setIsBackgroundLocked?.(false);
    };
  }, [ctx]);

  const next = () => {
    if (isLast) {
      onClose();
      return;
    }

    setDirection("next");

    setI((value) =>
      Math.min(STEPS.length - 1, value + 1)
    );
  };

  const previous = () => {
    if (isFirst) return;

    setDirection("prev");

    setI((value) =>
      Math.max(0, value - 1)
    );
  };

  const featureCount = step.features.length;
  const isThreeFeatures = featureCount === 3;

  return (
    <div
      className="
        fixed inset-0 z-[60]
        flex items-center justify-center
        bg-background/90
        px-4 py-6
        backdrop-blur-md
      "
      data-testid="tutorial-overlay"
    >
      {/* ═══════════════════════════════════════
          AMBIENT MAGIC
      ═══════════════════════════════════════ */}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="
            absolute left-1/2 top-1/2
            h-[550px] w-[550px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-gold/5
            blur-[110px]
          "
        />

        <div
          className="
            absolute left-[10%] top-[20%]
            h-40 w-40
            rounded-full
            bg-arcane/5
            blur-[80px]
          "
        />

        <div
          className="
            absolute bottom-[15%] right-[10%]
            h-32 w-32
            rounded-full
            bg-gold/5
            blur-[70px]
          "
        />
      </div>

      {/* ═══════════════════════════════════════
          MAIN GRIMOIRE
      ═══════════════════════════════════════ */}

      <div
        className="
          relative
          w-full max-w-2xl
          overflow-hidden
          rounded-2xl
          border border-gold/20
          bg-card
          shadow-[0_25px_100px_rgba(0,0,0,0.6)]
          rune-border
        "
      >
        {/* Top ornament */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

        {/* ═══════════════════════════════════════
            CLOSE
        ═══════════════════════════════════════ */}

        <button
          onClick={onClose}
          aria-label="Chiudi tutorial"
          data-testid="tutorial-close"
          className="
            absolute right-5 top-5 z-10
            flex h-9 w-9 items-center justify-center
            rounded-full
            border border-border
            bg-background/40
            text-muted-foreground
            transition-all
            hover:border-gold/30
            hover:bg-gold/5
            hover:text-gold
          "
        >
          <X className="h-5 w-5" />
        </button>

        {/* ═══════════════════════════════════════
            HEADER
        ═══════════════════════════════════════ */}

        <div className="relative px-6 pb-6 pt-9 sm:px-10 sm:pt-10">
          <div className="flex flex-col items-center text-center">
            {/* Main sigil */}
            <div
              className={`
                relative mb-5
                flex h-20 w-20
                items-center justify-center
                rounded-full
                border border-gold/30
                bg-gradient-to-b
                from-gold/10
                to-transparent
                ${step.accent}
                shadow-[0_0_40px_rgba(212,175,55,0.12)]
              `}
            >
              <div
                className="
                  absolute inset-2
                  rounded-full
                  border border-gold/10
                "
              />

              <div
                className="
                  absolute inset-0
                  rounded-full
                  ring-1
                  ring-inset
                  ring-white/5
                "
              />

              {step.icon}
            </div>

            <div
              className="
                mb-2
                font-heading
                text-[11px]
                font-semibold
                uppercase
                tracking-[0.28em]
                text-gold/70
              "
            >
              Cartomancer · Guida dell'avventuriero
            </div>

            <h2
              className="
                font-display
                text-3xl
                leading-tight
                text-gold
                sm:text-4xl
              "
            >
              {step.title}
            </h2>

            <p
              className="
                mt-2
                max-w-lg
                font-serif-fantasy
                text-lg
                italic
                leading-relaxed
                text-parchment/75
              "
            >
              {step.subtitle}
            </p>
          </div>

          {/* ═══════════════════════════════════════
              PROGRESS RUNES
          ═══════════════════════════════════════ */}

          <div className="mt-7 flex items-center justify-center gap-2">
            {STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(
                    index > i ? "next" : "prev"
                  );

                  setI(index);
                }}
                aria-label={`Vai al passo ${index + 1}`}
                className={`
                  transition-all
                  duration-300
                  ${
                    index === i
                      ? `
                        h-2.5
                        w-8
                        rounded-full
                        bg-gold
                        shadow-[0_0_12px_rgba(212,175,55,0.35)]
                      `
                      : index < i
                        ? `
                          h-2
                          w-2
                          rounded-full
                          bg-gold/50
                        `
                        : `
                          h-2
                          w-2
                          rounded-full
                          bg-border
                        `
                  }
                `}
              />
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════
            CONTENT
        ═══════════════════════════════════════ */}

        <div
          className="
            relative
            border-y border-border
            bg-background/20
            px-6 py-7
            sm:px-10 sm:py-8
          "
        >
          <div
            key={i}
            className={`
              animate-in
              fade-in
              duration-300
              ${
                direction === "next"
                  ? "slide-in-from-right-3"
                  : "slide-in-from-left-3"
              }
            `}
          >
            {/* ═════════════════════════════════════
                TRE ELEMENTI
                Layout ornamentale
            ═════════════════════════════════════ */}

            {isThreeFeatures ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {step.features.map((feature, index) => (
                  <div
                    key={index}
                    className="
                      group
                      relative
                      flex flex-col
                      items-center
                      overflow-hidden
                      rounded-xl
                      border border-gold/15
                      bg-card/50
                      px-5 py-6
                      text-center
                      transition-all
                      duration-300
                      hover:border-gold/30
                      hover:bg-gold/[0.025]
                      hover:shadow-[0_0_25px_rgba(212,175,55,0.05)]
                    "
                  >
                    {/* Corner ornaments */}
                    <div className="absolute left-2 top-2 h-3 w-3 border-l border-t border-gold/20" />
                    <div className="absolute right-2 top-2 h-3 w-3 border-r border-t border-gold/20" />
                    <div className="absolute bottom-2 left-2 h-3 w-3 border-b border-l border-gold/20" />
                    <div className="absolute bottom-2 right-2 h-3 w-3 border-b border-r border-gold/20" />

                    {/* Icon */}
                    <div
                      className="
                        mb-4
                        flex h-12 w-12
                        items-center justify-center
                        rounded-full
                        border border-gold/20
                        bg-gold/5
                        text-gold
                        shadow-[0_0_18px_rgba(212,175,55,0.08)]
                        transition-all
                        duration-300
                        group-hover:border-gold/40
                        group-hover:bg-gold/10
                      "
                    >
                      {feature.icon}
                    </div>

                    <h4
                      className="
                        font-heading
                        text-sm
                        font-semibold
                        uppercase
                        tracking-wide
                        text-parchment
                      "
                    >
                      {feature.title}
                    </h4>

                    <p
                      className="
                        mt-2
                        font-serif-fantasy
                        text-[16px]
                        leading-6
                        text-muted-foreground
                      "
                    >
                      {feature.text}
                    </p>

                    {/* Tiny rune */}
                    <div
                      className="
                        mt-4
                        h-1.5 w-1.5
                        rotate-45
                        bg-gold/40
                      "
                    />
                  </div>
                ))}
              </div>
            ) : (
              /* ═════════════════════════════════════
                 QUATTRO ELEMENTI
                 Layout 2 × 2
              ═════════════════════════════════════ */

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {step.features.map((feature, index) => (
                  <div
                    key={index}
                    className="
                      group
                      flex gap-4
                      rounded-xl
                      border border-border/60
                      bg-card/40
                      p-4
                      transition-all
                      duration-300
                      hover:border-gold/20
                      hover:bg-gold/[0.025]
                    "
                  >
                    <div
                      className="
                        mt-0.5
                        flex h-10 w-10
                        shrink-0
                        items-center justify-center
                        rounded-lg
                        border border-gold/15
                        bg-gold/5
                        text-gold
                        transition-all
                        group-hover:border-gold/30
                        group-hover:bg-gold/10
                      "
                    >
                      {feature.icon}
                    </div>

                    <div>
                      <h4
                        className="
                          font-heading
                          text-sm
                          font-semibold
                          uppercase
                          tracking-wide
                          text-parchment
                        "
                      >
                        {feature.title}
                      </h4>

                      <p
                        className="
                          mt-1
                          font-serif-fantasy
                          text-[16px]
                          leading-6
                          text-muted-foreground
                        "
                      >
                        {feature.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ═════════════════════════════════════
                PREMIUM SEAL
            ═════════════════════════════════════ */}

            {step.premium && (
              <div className="mt-6 flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.25em] text-arcane/70">
                <div className="h-px w-16 bg-gradient-to-r from-transparent to-arcane/30" />

                <Sparkles className="h-3.5 w-3.5" />

                <span>
                  {step.finalPremium
                    ? "Il grimorio è appena iniziato"
                    : "Cartomancer Premium"}
                </span>

                <Sparkles className="h-3.5 w-3.5" />

                <div className="h-px w-16 bg-gradient-to-l from-transparent to-arcane/30" />
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════
            FOOTER
        ═══════════════════════════════════════ */}

        <div
          className="
            flex flex-col
            gap-4
            px-6 py-5
            sm:flex-row
            sm:items-center
            sm:justify-between
            sm:px-10
          "
        >
          <button
            onClick={onDontShowAgain}
            data-testid="tutorial-dont-show"
            className="
              order-2
              self-center
              font-heading
              text-[11px]
              uppercase
              tracking-[0.16em]
              text-muted-foreground
              transition-colors
              hover:text-gold
              sm:order-1
              sm:self-auto
            "
          >
            Non mostrare più
          </button>

          <div
            className="
              order-1
              flex
              justify-center
              gap-2
              sm:order-2
            "
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={previous}
              disabled={isFirst}
              data-testid="tutorial-prev"
            >
              <ChevronLeft className="mr-1.5 h-4 w-4" />
              Indietro
            </Button>

            {isLast ? (
              <Button
                variant="primary"
                size="sm"
                onClick={onClose}
                data-testid="tutorial-finish"
              >
                Inizia l'avventura
                <Sparkles className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={next}
                data-testid="tutorial-next"
              >
                Avanti
                <ChevronRight className="ml-1.5 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Bottom ornament */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      </div>
    </div>
  );
}